const UNKNOWN_USER_KEY = "unknown-user";
const UNKNOWN_TRIAL_KEY = "unknown-trial";
const WORKSPACE_SELECTION_PREFIX = "workspace-selection";
const WORKSPACE_STATE_PREFIX = "workspace-state";
const WORKSPACE_DRAFT_PREFIX = "workspace-draft";
const WORKSPACE_STORAGE_VERSION_KEY = "workspace-storage-version";

export const CURRENT_WORKSPACE_STORAGE_VERSION = "3";

export function getWorkspaceStorageVersionKey(userId: string) {
  return `${WORKSPACE_STORAGE_VERSION_KEY}-${userId}`;
}

export function getCodedUserKey(email?: string) {
  if (!email) return UNKNOWN_USER_KEY;

  return email
    .toLowerCase()
    .split("")
    .map((char) => char.charCodeAt(0).toString(16))
    .join("");
}

/**
 * Prefers the stable user id over the hashed email for scoping storage keys:
 * emails aren't guaranteed stable (a user can change theirs), so hashed-email
 * keys can silently orphan a user's prior sessions. Falls back to the hashed
 * email when no id is available (e.g. before the user profile has loaded).
 */
export function getWorkspaceUserStorageKey(user?: {
  id?: string;
  email?: string;
}) {
  if (user?.id) return user.id;
  return getCodedUserKey(user?.email);
}

export function getWorkspaceSelectionStorageKey(
  trialId: string,
  tableName: string,
  userKey: string,
) {
  return `${WORKSPACE_SELECTION_PREFIX}-${trialId || UNKNOWN_TRIAL_KEY}-${tableName}-${userKey}`;
}

export function getLegacyWorkspaceSelectionStorageKey(
  tableName: string,
  userKey: string,
) {
  return `${WORKSPACE_SELECTION_PREFIX}-${tableName}-${userKey}`;
}

export function getDataSectionWorkspaceSelectionStorageKey(
  trialId: string,
  tableName: string,
  dataSection: string,
  userKey: string,
) {
  return `${WORKSPACE_SELECTION_PREFIX}-${trialId || UNKNOWN_TRIAL_KEY}-${tableName}-${dataSection}-${userKey}`;
}

// TODO(VIVO-7XXX): Remove this function and the migration logic in readMigratedLocalStorageItem after v1.18.0 release cycle
// (approximately 1-2 releases after June 2026).
// This function handles the storage key format used in v1.18.0 (workspace-${trialId}-${tableName}).
// Added to prevent data loss when users upgrade from v1.18.0 to subsequent versions that use email-encoded keys.
export function getOlderLegacyWorkspaceSelectionStorageKey(
  trialId: string,
  tableName: string,
) {
  return `workspace-${trialId}-${tableName}`;
}

/**
 * The current key for a page's in-progress (unsaved) table-state draft.
 * Named "draft" (not "state") to make clear this is a local scratchpad that
 * shadows the saved workspace, not the source of truth for it.
 */
export function getWorkspaceDraftStorageKey(
  trialId: string,
  pageName: string,
  userKey: string,
) {
  return `${WORKSPACE_DRAFT_PREFIX}-${trialId || UNKNOWN_TRIAL_KEY}-${pageName}-${userKey}`;
}

// Alias of the pre-rename "workspace-state-*" key, kept so existing full
// caches migrate into the new draft key on first read.
export function getLegacyWorkspaceDraftStorageKey(
  trialId: string,
  pageName: string,
  userKey: string,
) {
  return `${WORKSPACE_STATE_PREFIX}-${trialId || UNKNOWN_TRIAL_KEY}-${pageName}-${userKey}`;
}

// Alias of the oldest (pre-trial-scoping) cache key format.
export function getOlderLegacyWorkspaceDraftStorageKey(
  pageName: string,
  userKey: string,
) {
  return `${pageName}-${userKey}`;
}

// Three-tier migration strategy for workspace selection/draft storage keys.
// Checks in order: new format → legacy format(s) → older format.
export function readMigratedLocalStorageItem(
  storageKey: string,
  legacyStorageKey: string | Array<string | undefined>,
  olderLegacyStorageKey?: string,
) {
  const currentValue = localStorage.getItem(storageKey);
  if (currentValue !== null) {
    return currentValue;
  }

  const legacyKeys = (
    Array.isArray(legacyStorageKey) ? legacyStorageKey : [legacyStorageKey]
  ).filter((key): key is string => Boolean(key));

  for (const legacyKey of legacyKeys) {
    const legacyValue = localStorage.getItem(legacyKey);
    if (legacyValue !== null) {
      localStorage.setItem(storageKey, legacyValue);
      // Clean up every legacy candidate once we've migrated from one of them,
      // not just the one we read from.
      legacyKeys.forEach((key) => localStorage.removeItem(key));
      return legacyValue;
    }
  }

  if (olderLegacyStorageKey) {
    const olderLegacyValue = localStorage.getItem(olderLegacyStorageKey);
    if (olderLegacyValue !== null) {
      localStorage.setItem(storageKey, olderLegacyValue);
      localStorage.removeItem(olderLegacyStorageKey);
      return olderLegacyValue;
    }
  }

  return null;
}

/**
 * Drops a stored item if it isn't valid JSON, so a corrupted cache can't
 * permanently break hydration for a page. Returns whether it removed
 * anything.
 */
export function removeMalformedWorkspaceStorageItem(
  key: string,
  value: string | null,
) {
  if (value === null) return false;
  try {
    JSON.parse(value);
    return false;
  } catch {
    localStorage.removeItem(key);
    return true;
  }
}

export function removeWorkspaceStorageItems(keys: string[]) {
  keys.forEach((key) => localStorage.removeItem(key));
}

export function markWorkspaceStorageMigrated(userId: string) {
  localStorage.setItem(
    getWorkspaceStorageVersionKey(userId),
    CURRENT_WORKSPACE_STORAGE_VERSION,
  );
}

const workspaceStorageKeyPrefixes = [
  WORKSPACE_SELECTION_PREFIX,
  WORKSPACE_STATE_PREFIX,
  WORKSPACE_DRAFT_PREFIX,
];

/**
 * One-time migration from the hashed-email-keyed storage scheme to the
 * user-id-keyed one: rewrites every workspace-selection/-state/-draft key
 * that ends in this user's hashed-email suffix to end in their id instead,
 * and purges anything stranded under the `unknown-user` bucket (data that
 * was written before the user's identity was known and can't be reliably
 * reattributed). No-ops once already migrated (tracked via the user's
 * `workspace-storage-version-${user.id}` key) or when the user id isn't known
 * yet.
 */
export function migrateLegacyWorkspaceStorageForUser(user?: {
  id?: string;
  email?: string;
}) {
  if (!user?.id) return;
  if (
    localStorage.getItem(getWorkspaceStorageVersionKey(user.id)) ===
    CURRENT_WORKSPACE_STORAGE_VERSION
  ) {
    return;
  }

  const codedEmail = getCodedUserKey(user.email);
  if (codedEmail !== UNKNOWN_USER_KEY) {
    const emailSuffix = `-${codedEmail}`;
    const keysToMigrate: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (
        key &&
        key.endsWith(emailSuffix) &&
        workspaceStorageKeyPrefixes.some((prefix) => key.startsWith(prefix))
      ) {
        keysToMigrate.push(key);
      }
    }

    keysToMigrate.forEach((oldKey) => {
      const newKey = `${oldKey.slice(0, -emailSuffix.length)}-${user.id}`;
      const value = localStorage.getItem(oldKey);
      if (value !== null && localStorage.getItem(newKey) === null) {
        localStorage.setItem(newKey, value);
      }
      localStorage.removeItem(oldKey);
    });
  }

  const unknownUserSuffix = `-${UNKNOWN_USER_KEY}`;
  const unknownUserKeys: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.endsWith(unknownUserSuffix)) {
      unknownUserKeys.push(key);
    }
  }
  removeWorkspaceStorageItems(unknownUserKeys);

  markWorkspaceStorageMigrated(user.id);
}
